import {
  COMPANY_NC_DEPARTMENTS,
  COMPANY_NC_SOURCES,
  COMPANY_NC_STATUSES,
  type CompanyNcInput,
  type CompanyNcStatus,
  HSE_ACTION_KINDS,
  NC_CLASSES,
  parseCompanyNc,
} from '@felix/domain';
import {
  Button,
  Group,
  Loader,
  Modal,
  SegmentedControl,
  Select,
  Textarea,
  TextInput,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconChevronRight,
  IconCircleCheck,
  IconClipboardList,
  IconClock,
  IconDeviceFloppy,
  IconHourglass,
  IconPlus,
  IconPrinter,
  IconSearch,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../../EmptyState.js';
import { formatDateTime, formatDay } from '../../../format.js';
import { DayInput } from '../../../inputs/DayInput.js';
import { PageHeader } from '../../../PageHeader.js';
import { Panel } from '../../../Panel.js';
import { StatTile } from '../../../StatTile.js';
import { StatusBadge } from '../../../StatusBadge.js';
import { useUnsavedChanges } from '../../../shell-context.js';
import table from '../../materials/Materials.module.css';
import {
  NoMatch,
  SortableTh,
  type SortState,
  TablePager,
  TableSkeleton,
} from '../../purchasing/table.js';
import { recordPdfFileName } from '../../reports/record-pdf.js';
import { type RecordPdfOutput, useRecordPrint } from '../../reports/record-print.js';
import { ConfirmDialog } from '../Dialogs.js';
import classes from '../Hse.module.css';
import { HseLoadError, HseStepBadge, hseErrorText } from '../parts.js';
import { companyNcPdf } from './company-nc-pdf.js';
import type {
  CompanyNcAction,
  CompanyNcActionInput,
  CompanyNcDetail,
  CompanyNcList as CompanyNcListResult,
  CompanyNcSource,
  CompanyNcSummary,
} from './types.js';

/**
 * Ofisin doğrudan açtığı şirket uygunsuzluğu (ADR 0062 §2) [ISM 9, 12]: Non Conformity ekranında
 * "Vessel reports | Company (office)" seçimi, liste (özet kartları, arama, tablo, sayfalama) ve
 * kayıt (genel bilgi, kök neden, faaliyetler, doğrulama ve kapanış, geçmiş, Print / PDF). Yalnız
 * web ofiste; kayıt gemiye inmez. Görünüm HSE ekranlarının dilini kullanır (gemi-hse-genel.jpeg).
 */

export function NcSegment({
  value,
  onChange,
}: {
  value: 'vessel' | 'company';
  onChange: (next: 'vessel' | 'company') => void;
}) {
  const { t } = useTranslation();
  return (
    <SegmentedControl
      aria-label={t('companyNc.segment.label')}
      value={value}
      onChange={(next) => onChange(next as 'vessel' | 'company')}
      data={[
        { value: 'vessel', label: t('companyNc.segment.vessel') },
        { value: 'company', label: t('companyNc.segment.company') },
      ]}
      style={{ alignSelf: 'flex-start' }}
    />
  );
}

const STATUS_TONE: Record<CompanyNcStatus, 'success' | 'warning' | 'danger' | 'info'> = {
  open: 'danger',
  reopened: 'danger',
  action_assigned: 'warning',
  in_progress: 'warning',
  pending_verification: 'info',
  closed: 'success',
};

export function CompanyNcStatusBadge({ status }: { status: CompanyNcStatus }) {
  const { t } = useTranslation();
  return <StatusBadge tone={STATUS_TONE[status]}>{t(`companyNc.statuses.${status}`)}</StatusBadge>;
}

type Column = 'ncNo' | 'detected' | 'vessel' | 'source' | 'ncClass' | 'title' | 'status' | 'target';
const COLUMNS: readonly Column[] = [
  'ncNo',
  'detected',
  'vessel',
  'source',
  'ncClass',
  'title',
  'status',
  'target',
];
const PAGE_SIZE = 10;

type StatusFilter = CompanyNcStatus | 'open_any' | 'overdue' | null;

function sortValue(item: CompanyNcSummary, column: Column): string {
  switch (column) {
    case 'ncNo':
      return item.ncNo;
    case 'detected':
      return item.detectedOn;
    case 'vessel':
      return item.vesselName ?? '';
    case 'source':
      return item.source;
    case 'ncClass':
      return item.ncClass;
    case 'title':
      return item.title.toLocaleLowerCase();
    case 'status':
      return item.status;
    case 'target':
      return item.targetDate ?? '9999';
  }
}

export function CompanyNcList({
  source,
  segment,
  onOpen,
  onNew,
}: {
  source: CompanyNcSource;
  segment: React.ReactNode;
  onOpen: (id: string) => void;
  onNew: () => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`companyNc.list.${key}`, options);
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'error'; error: unknown }
    | { kind: 'ready'; list: CompanyNcListResult }
  >({ kind: 'loading' });
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>(null);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState<Column>>({ key: 'detected', dir: 'desc' });

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      setState({ kind: 'ready', list: await source.list() });
    } catch (error) {
      setState({ kind: 'error', error });
    }
  }, [source]);
  useEffect(() => {
    void load();
  }, [load]);

  const ready = state.kind === 'ready' ? state.list : null;
  const items = useMemo(() => {
    if (!ready) return [];
    const words = search.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
    const filtered = ready.items.filter((item) => {
      if (status === 'open_any' && item.status === 'closed') return false;
      if (status === 'overdue' && !item.overdue) return false;
      if (status && status !== 'open_any' && status !== 'overdue' && item.status !== status)
        return false;
      const text = [
        item.ncNo,
        item.title,
        item.vesselName ?? '',
        item.detectedBy,
        item.responsible ?? '',
      ]
        .join(' ')
        .toLocaleLowerCase();
      return words.every((word) => text.includes(word));
    });
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort(
      (a, b) =>
        dir * sortValue(a, sort.key).localeCompare(sortValue(b, sort.key)) ||
        b.ncNo.localeCompare(a.ncNo),
    );
  }, [ready, search, status, sort]);
  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = items.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const kpis = ready?.kpis;
  const tile = (
    key: 'total' | 'open' | 'pendingVerification' | 'overdue' | 'closed',
    filter: StatusFilter,
    icon: React.ReactNode,
    tone: 'default' | 'success' | 'warning' | 'danger',
  ) => (
    <button
      type="button"
      className={classes.tileButton}
      aria-pressed={status === filter && filter !== null}
      onClick={() => {
        setStatus(status === filter ? null : filter);
        setPage(1);
      }}
    >
      <StatTile icon={icon} label={k(`kpis.${key}`)} value={kpis ? kpis[key] : '—'} tone={tone} />
    </button>
  );

  return (
    <div className={classes.screen}>
      <PageHeader
        title={k('title')}
        description={k('description')}
        actions={
          ready?.canCreate ? (
            <Button leftSection={<IconPlus size={16} />} onClick={onNew}>
              {k('new')}
            </Button>
          ) : null
        }
      />
      {segment}
      <div className={classes.tiles}>
        {tile('total', null, <IconClipboardList size={20} />, 'default')}
        {tile('open', 'open_any', <IconAlertTriangle size={20} />, 'danger')}
        {tile(
          'pendingVerification',
          'pending_verification',
          <IconHourglass size={20} />,
          'warning',
        )}
        {tile('overdue', 'overdue', <IconClock size={20} />, 'danger')}
        {tile('closed', 'closed', <IconCircleCheck size={20} />, 'success')}
      </div>
      <Panel flush aria-label={k('table')}>
        <form
          className={classes.toolbar}
          aria-label={k('table')}
          onSubmit={(event) => event.preventDefault()}
        >
          <TextInput
            label={k('search')}
            placeholder={k('searchPlaceholder')}
            leftSection={<IconSearch size={16} aria-hidden />}
            value={search}
            onChange={(event) => {
              setSearch(event.currentTarget.value);
              setPage(1);
            }}
          />
          <Select
            label={k('status')}
            data={[
              { value: '', label: k('allStatuses') },
              ...COMPANY_NC_STATUSES.map((value) => ({
                value,
                label: t(`companyNc.statuses.${value}`),
              })),
            ]}
            value={
              status && (COMPANY_NC_STATUSES as readonly string[]).includes(status) ? status : ''
            }
            allowDeselect={false}
            onChange={(value) => {
              setStatus((value || null) as StatusFilter);
              setPage(1);
            }}
          />
        </form>
        {state.kind === 'error' ? (
          <HseLoadError error={state.error} onRetry={() => void load()} />
        ) : state.kind === 'loading' ? (
          <TableSkeleton columns={8} label={k('table')} />
        ) : (ready?.items.length ?? 0) === 0 ? (
          <EmptyState title={k('emptyTitle')} description={k('emptyHint')} />
        ) : items.length === 0 ? (
          <NoMatch
            title={k('noMatch')}
            onClear={() => {
              setSearch('');
              setStatus(null);
            }}
          />
        ) : (
          <>
            <section
              className={table.tableWrap}
              aria-label={k('table')}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
            >
              <table className={`${table.table} ${classes.table}`}>
                <thead>
                  <tr>
                    {COLUMNS.map((column) => (
                      <SortableTh
                        key={column}
                        column={column}
                        label={k(`columns.${column === 'ncNo' ? 'no' : column}`)}
                        sort={sort}
                        onSort={setSort}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visible.map((item) => (
                    <tr key={item.id}>
                      <td className={classes.key}>
                        <a
                          className={classes.link}
                          href={`?company=${item.id}`}
                          aria-label={k('open', { no: item.ncNo })}
                          onClick={(event) => {
                            event.preventDefault();
                            onOpen(item.id);
                          }}
                        >
                          {item.ncNo}
                        </a>
                      </td>
                      <td className={classes.nowrap}>{formatDay(item.detectedOn)}</td>
                      <td>{item.vesselName ?? t('companyNc.noVessel')}</td>
                      <td>{t(`companyNc.sources.${item.source}`)}</td>
                      <td>{t(`hse.ncClasses.${item.ncClass}`)}</td>
                      <td className={classes.wrap}>{item.title}</td>
                      <td>
                        <CompanyNcStatusBadge status={item.status} />
                        {item.overdue ? (
                          <span className={classes.overdue}>
                            <span className={classes.muted}>
                              <IconClock size={14} aria-hidden /> {k('overdue')}
                            </span>
                          </span>
                        ) : null}
                      </td>
                      <td className={classes.nowrap}>
                        {item.targetDate ? formatDay(item.targetDate) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            <TablePager
              page={current}
              pages={pages}
              summary={k('showing', {
                from: (current - 1) * PAGE_SIZE + 1,
                to: Math.min(current * PAGE_SIZE, items.length),
                total: items.length,
              })}
              label={k('pagination')}
              onPage={setPage}
            />
          </>
        )}
      </Panel>
    </div>
  );
}

/* ---------- Kayıt ---------- */

const EMPTY: CompanyNcInput = {
  title: '',
  description: '',
  source: 'internal_audit',
  ncClass: 'minor',
  smsReference: null,
  department: null,
  detectedOn: '',
  detectedBy: '',
  vesselId: null,
  immediateAction: null,
  rootCause: null,
  verificationNote: null,
  responsible: null,
  targetDate: null,
};

function inputOf(detail: CompanyNcDetail): CompanyNcInput {
  return {
    title: detail.title,
    description: detail.description,
    source: detail.source as CompanyNcInput['source'],
    ncClass: detail.ncClass as CompanyNcInput['ncClass'],
    smsReference: detail.smsReference,
    department: detail.department as CompanyNcInput['department'],
    detectedOn: detail.detectedOn,
    detectedBy: detail.detectedBy,
    vesselId: detail.vesselId,
    immediateAction: detail.immediateAction,
    rootCause: detail.rootCause,
    verificationNote: detail.verificationNote,
    responsible: detail.responsible,
    targetDate: detail.targetDate,
  };
}

type Dialog =
  | null
  | { kind: 'action'; action: CompanyNcAction | null }
  | { kind: 'complete'; action: CompanyNcAction }
  | { kind: 'cancelAction'; action: CompanyNcAction }
  | { kind: 'close' }
  | { kind: 'reopen' };

export function CompanyNcRecord({
  source,
  output,
  id,
  onBack,
  onOpen,
}: {
  source: CompanyNcSource;
  output: RecordPdfOutput;
  id: string | null;
  onBack: () => void;
  onOpen: (id: string) => void;
}) {
  const { t } = useTranslation();
  const c = (key: string, options: Record<string, unknown> = {}) => t(`companyNc.${key}`, options);
  const [detail, setDetail] = useState<CompanyNcDetail | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  // Yeni kayıtta tespit günü bugün (açılış anında)
  const [value, setValue] = useState<CompanyNcInput>(() => ({
    ...EMPTY,
    detectedOn: new Date().toISOString().slice(0, 10),
  }));
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ tone: 'danger' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [vessels, setVessels] = useState<{ id: string; name: string }[]>([]);
  const [openAfterSave, setOpenAfterSave] = useState<string | null>(null);
  const printer = useRecordPrint(output);
  useUnsavedChanges(dirty);
  useEffect(() => {
    if (openAfterSave && !dirty) onOpen(openAfterSave);
  }, [openAfterSave, dirty, onOpen]);

  const apply = useCallback((next: CompanyNcDetail) => {
    setDetail(next);
    setValue(inputOf(next));
    setDirty(false);
    setErrors({});
  }, []);
  const load = useCallback(async () => {
    if (!id) return;
    setLoadError(null);
    try {
      apply(await source.detail(id));
    } catch (error) {
      setLoadError(error);
    }
  }, [apply, id, source]);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    source
      .list()
      .then((list) => setVessels(list.vessels))
      .catch(() => setVessels([]));
  }, [source]);

  if (id && loadError) return <HseLoadError error={loadError} onRetry={() => void load()} />;
  if (id && !detail) {
    return (
      <Group justify="center" p="xl">
        <Loader aria-label={c('record.loading')} />
      </Group>
    );
  }

  const isNew = detail === null;
  const can = detail?.can;
  const readOnly = !isNew && !can?.edit;
  const change = (next: Partial<CompanyNcInput>) => {
    setValue((v) => ({ ...v, ...next }));
    setDirty(true);
  };
  const fieldError = (path: string) =>
    errors[path]
      ? t(`hse.fieldErrors.${errors[path]}`, { defaultValue: t('hse.fieldErrors.INVALID') })
      : undefined;

  const run = async (work: () => Promise<CompanyNcDetail | undefined>, done?: string) => {
    setBusy(true);
    setMessage(null);
    try {
      const next = await work();
      if (next) apply(next);
      if (done) setMessage({ tone: 'success', text: done });
    } catch (error) {
      setMessage({ tone: 'danger', text: hseErrorText(t, error) });
      throw error;
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    const parsed = parseCompanyNc(value);
    if (!parsed.ok) {
      setErrors(Object.fromEntries(parsed.issues.map((i) => [i.path, i.code])));
      setMessage({ tone: 'danger', text: c('record.fixFields') });
      return;
    }
    await run(async () => {
      if (!detail) {
        const created = await source.open(parsed.value);
        setDirty(false);
        setOpenAfterSave(created.id);
        return created;
      }
      return source.update(detail.id, detail.rowVersion, parsed.value);
    }, c('record.saved')).catch(() => undefined);
  };

  const print = async () => {
    if (!detail) return;
    const failure = await printer.print((tr, brand) => ({
      definition: companyNcPdf(tr, { detail, brand, printedAt: new Date().toISOString() }),
      fileName: recordPdfFileName(detail.ncNo, c('pdf.title')),
    }));
    if (failure) setMessage({ tone: 'danger', text: failure });
  };

  const select = (
    key: 'source' | 'ncClass' | 'department',
    options: readonly string[],
    label: (value: string) => string,
    required: boolean,
  ) => (
    <Select
      label={c(`fields.${key}`)}
      required={required}
      readOnly={readOnly}
      data={options.map((option) => ({ value: option, label: label(option) }))}
      value={(value[key] as string | null) ?? null}
      clearable={!required && !readOnly}
      error={fieldError(key)}
      {...(readOnly ? { rightSection: null } : {})}
      onChange={(next) => change({ [key]: next ?? null } as Partial<CompanyNcInput>)}
    />
  );
  const text = (key: 'title' | 'smsReference' | 'detectedBy' | 'responsible', required = false) => (
    <TextInput
      label={c(`fields.${key}`)}
      required={required}
      readOnly={readOnly}
      maxLength={key === 'smsReference' ? 300 : 200}
      value={value[key] ?? ''}
      error={fieldError(key)}
      onChange={(event) => change({ [key]: event.currentTarget.value } as Partial<CompanyNcInput>)}
    />
  );
  const area = (
    key: 'description' | 'immediateAction' | 'rootCause' | 'verificationNote',
    required = false,
    max = 4000,
  ) => (
    <div className={classes.full}>
      <Textarea
        label={c(`fields.${key}`)}
        required={required}
        readOnly={readOnly}
        autosize
        minRows={2}
        maxRows={10}
        maxLength={max}
        value={value[key] ?? ''}
        error={fieldError(key)}
        onChange={(event) =>
          change({ [key]: event.currentTarget.value || null } as Partial<CompanyNcInput>)
        }
      />
    </div>
  );

  const header = (
    <>
      <nav className={classes.crumbs} aria-label={c('record.breadcrumb')}>
        <button type="button" className={classes.link} onClick={onBack}>
          {c('list.title')}
        </button>
        <IconChevronRight size={14} aria-hidden />
        <span>{detail ? detail.ncNo : c('record.newTitle')}</span>
      </nav>
      <div className={classes.recordHead}>
        <div className={classes.recordTitle}>
          <h1>{detail ? `${detail.ncNo} · ${detail.title}` : c('record.newTitle')}</h1>
          {detail ? <CompanyNcStatusBadge status={detail.status} /> : null}
        </div>
        <div className={classes.recordActions}>
          {isNew || (can?.edit && dirty) ? (
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              loading={busy}
              onClick={() => void save()}
            >
              {isNew ? c('record.create') : c('record.save')}
            </Button>
          ) : null}
          {detail && can?.export ? (
            <Button
              variant="default"
              leftSection={<IconPrinter size={16} />}
              loading={printer.printing}
              disabled={dirty}
              onClick={() => void print()}
            >
              {t('recordPdf.print')}
            </Button>
          ) : null}
          {detail && can?.close ? (
            <Button color="green" disabled={dirty} onClick={() => setDialog({ kind: 'close' })}>
              {c('closure.close')}
            </Button>
          ) : null}
          {detail && can?.reopen ? (
            <Button variant="default" onClick={() => setDialog({ kind: 'reopen' })}>
              {c('closure.reopen')}
            </Button>
          ) : null}
        </div>
      </div>
      {message ? (
        <p
          className={classes.banner}
          data-tone={message.tone}
          role={message.tone === 'danger' ? 'alert' : 'status'}
        >
          {message.text}
        </p>
      ) : null}
    </>
  );

  const general = (
    <Panel title={c('record.generalTitle')} className={classes.card}>
      <div className={classes.cardBody}>
        <div className={classes.grid3}>
          <div className={classes.full}>{text('title', true)}</div>
          {select('source', COMPANY_NC_SOURCES, (v) => c(`sources.${v}`), true)}
          {select('ncClass', NC_CLASSES, (v) => t(`hse.ncClasses.${v}`), true)}
          {select('department', COMPANY_NC_DEPARTMENTS, (v) => c(`departments.${v}`), false)}
          <DayInput
            label={c('fields.detectedOn')}
            required
            readOnly={readOnly}
            value={value.detectedOn}
            maxDate={new Date().toISOString().slice(0, 10)}
            error={fieldError('detectedOn')}
            onChange={(next) => change({ detectedOn: next ?? '' })}
          />
          {text('detectedBy', true)}
          <Select
            label={c('fields.vessel')}
            readOnly={readOnly}
            placeholder={c('noVessel')}
            data={vessels.map((v) => ({ value: v.id, label: v.name }))}
            value={value.vesselId}
            clearable={!readOnly}
            error={fieldError('vesselId')}
            {...(readOnly ? { rightSection: null } : {})}
            onChange={(next) => change({ vesselId: next ?? null })}
          />
          <div className={classes.full}>{text('smsReference')}</div>
          {area('description', true, 8000)}
          {area('immediateAction')}
        </div>
        <p className={classes.muted}>{c('record.officeOnly')}</p>
      </div>
    </Panel>
  );

  const investigation = (
    <Panel title={c('record.investigationTitle')} className={classes.card}>
      <div className={classes.grid2}>
        {area('rootCause')}
        {text('responsible')}
        <DayInput
          label={c('fields.targetDate')}
          readOnly={readOnly}
          value={value.targetDate}
          error={fieldError('targetDate')}
          onChange={(next) => change({ targetDate: next })}
        />
      </div>
    </Panel>
  );

  return (
    <div className={classes.screen}>
      {header}
      <div className={classes.layout}>
        <div className={classes.main}>
          {general}
          {investigation}
          {detail ? (
            <ActionsPanel
              detail={detail}
              busy={busy}
              onAdd={() => setDialog({ kind: 'action', action: null })}
              onEdit={(action) => setDialog({ kind: 'action', action })}
              onStart={(action) =>
                void run(() =>
                  source.changeAction(action.id, action.rowVersion, { op: 'start' }),
                ).catch(() => undefined)
              }
              onComplete={(action) => setDialog({ kind: 'complete', action })}
              onCancel={(action) => setDialog({ kind: 'cancelAction', action })}
            />
          ) : null}
          {detail ? (
            <Panel title={c('closure.title')} className={classes.card}>
              <div className={classes.cardBody}>
                <div className={classes.grid2}>{area('verificationNote')}</div>
                {detail.status === 'closed' ? null : detail.gaps.length === 0 ? (
                  <p className={classes.muted}>{c('closure.ready')}</p>
                ) : (
                  <div>
                    <p className={classes.muted}>{c('closure.gapsTitle')}</p>
                    <ul className={classes.gapList}>
                      {detail.gaps.map((gap) => (
                        <li key={gap}>{c(`closure.gaps.${gap}`, { defaultValue: gap })}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {detail.status !== 'closed' && !can?.close && detail.gaps.length === 0 ? (
                  <p className={classes.muted}>{c('closure.selfClose')}</p>
                ) : null}
              </div>
            </Panel>
          ) : null}
        </div>
        <aside className={classes.rail} aria-label={c('events.title')}>
          <Panel title={c('events.title')}>
            {detail ? (
              <ul className={classes.events}>
                {detail.events.map((event) => (
                  <li key={event.id}>
                    <strong>{c(`events.${event.kind}`)}</strong>
                    <div className={classes.muted}>
                      {formatDateTime(event.at)} · {detail.names[event.by] ?? event.by}
                      {event.roleIds.length > 0
                        ? ` (${event.roleIds.map((r) => detail.roles[r] ?? r).join(', ')})`
                        : ''}
                    </div>
                    {event.note ? <div>{event.note}</div> : null}
                  </li>
                ))}
                <li>
                  <strong>{c('events.opened')}</strong>
                  <div className={classes.muted}>
                    {formatDateTime(detail.createdAt)} ·{' '}
                    {detail.names[detail.createdBy] ?? detail.createdBy}
                  </div>
                </li>
              </ul>
            ) : (
              <p className={classes.muted}>{c('events.none')}</p>
            )}
          </Panel>
        </aside>
      </div>

      {detail && dialog?.kind === 'action' ? (
        <ActionDialog
          action={dialog.action}
          onClose={() => setDialog(null)}
          onSave={(input) =>
            run(() =>
              dialog.action
                ? source.changeAction(dialog.action.id, dialog.action.rowVersion, {
                    op: 'edit',
                    input,
                  })
                : source.addAction(detail.id, input),
            )
          }
        />
      ) : null}
      {detail ? (
        <>
          <ConfirmDialog
            opened={dialog?.kind === 'complete'}
            title={c('actions.completeTitle')}
            body={c('actions.noteHint')}
            reason="required"
            reasonLabel={c('actions.note')}
            password={false}
            confirmLabel={c('actions.complete')}
            onClose={() => setDialog(null)}
            onConfirm={({ reason }) =>
              dialog?.kind === 'complete'
                ? run(() =>
                    source.changeAction(dialog.action.id, dialog.action.rowVersion, {
                      op: 'complete',
                      note: reason,
                    }),
                  )
                : Promise.resolve()
            }
          />
          <ConfirmDialog
            opened={dialog?.kind === 'cancelAction'}
            title={c('actions.cancelTitle')}
            body={c('actions.cancel')}
            reason="required"
            reasonLabel={c('actions.reason')}
            password={false}
            confirmLabel={c('actions.cancel')}
            color="red"
            onClose={() => setDialog(null)}
            onConfirm={({ reason }) =>
              dialog?.kind === 'cancelAction'
                ? run(() =>
                    source.changeAction(dialog.action.id, dialog.action.rowVersion, {
                      op: 'cancel',
                      reason,
                    }),
                  )
                : Promise.resolve()
            }
          />
          <ConfirmDialog
            opened={dialog?.kind === 'close'}
            title={c('closure.closeTitle')}
            body={c('closure.closeText')}
            reason="optional"
            reasonLabel={c('closure.note')}
            password
            reauthKind="company_nonconformity"
            confirmLabel={c('closure.confirmClose')}
            color="green"
            onClose={() => setDialog(null)}
            onConfirm={({ reason, password }) =>
              run(
                () =>
                  source.close(detail.id, {
                    note: reason || null,
                    ...(password ? { password } : {}),
                  }),
                c('closure.closed'),
              )
            }
          />
          <ConfirmDialog
            opened={dialog?.kind === 'reopen'}
            title={c('closure.reopenTitle')}
            body={c('closure.reopenText')}
            reason="required"
            reasonLabel={c('closure.reason')}
            password
            reauthKind="company_nonconformity"
            confirmLabel={c('closure.confirmReopen')}
            onClose={() => setDialog(null)}
            onConfirm={({ reason, password }) =>
              run(
                () => source.reopen(detail.id, { reason, ...(password ? { password } : {}) }),
                c('closure.reopened'),
              )
            }
          />
        </>
      ) : null}
    </div>
  );
}

function ActionsPanel({
  detail,
  busy,
  onAdd,
  onEdit,
  onStart,
  onComplete,
  onCancel,
}: {
  detail: CompanyNcDetail;
  busy: boolean;
  onAdd: () => void;
  onEdit: (action: CompanyNcAction) => void;
  onStart: (action: CompanyNcAction) => void;
  onComplete: (action: CompanyNcAction) => void;
  onCancel: (action: CompanyNcAction) => void;
}) {
  const { t } = useTranslation();
  const a = (key: string) => t(`companyNc.actions.${key}`);
  const live = new Set(detail.can.actionIds);
  return (
    <Panel
      title={a('title')}
      className={classes.card}
      extra={
        detail.can.actions ? (
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={onAdd}>
            {a('add')}
          </Button>
        ) : undefined
      }
    >
      {detail.actions.length === 0 ? (
        <p className={classes.muted}>{a('none')}</p>
      ) : (
        <section
          className={table.tableWrap}
          aria-label={a('title')}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
          tabIndex={0}
        >
          <table className={`${table.table} ${classes.table}`}>
            <thead>
              <tr>
                <th>#</th>
                <th>{a('kind')}</th>
                <th>{a('description')}</th>
                <th>{a('responsible')}</th>
                <th>{a('due')}</th>
                <th>{a('status')}</th>
                <th aria-label={a('edit')} />
              </tr>
            </thead>
            <tbody>
              {detail.actions.map((action) => (
                <tr key={action.id}>
                  <td>{action.actionNo}</td>
                  <td>{t(`hse.actionKinds.${action.kind}`)}</td>
                  <td className={classes.wrap}>
                    {action.description}
                    {action.completionNote ? (
                      <div className={classes.muted}>
                        {a('completion')}: {action.completionNote}
                      </div>
                    ) : null}
                    {action.cancelReason ? (
                      <div className={classes.muted}>
                        {a('cancelled')}: {action.cancelReason}
                      </div>
                    ) : null}
                  </td>
                  <td>{action.responsible}</td>
                  <td className={classes.nowrap}>{formatDay(action.dueDate)}</td>
                  <td>
                    <HseStepBadge status={action.status} kind="action" />
                  </td>
                  <td>
                    {live.has(action.id) ? (
                      <Group gap={4} wrap="nowrap">
                        {action.status === 'planned' ? (
                          <Button
                            size="compact-xs"
                            variant="light"
                            disabled={busy}
                            onClick={() => onStart(action)}
                          >
                            {a('start')}
                          </Button>
                        ) : null}
                        <Button
                          size="compact-xs"
                          disabled={busy}
                          onClick={() => onComplete(action)}
                        >
                          {a('complete')}
                        </Button>
                        <Button size="compact-xs" variant="subtle" onClick={() => onEdit(action)}>
                          {a('edit')}
                        </Button>
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          color="red"
                          onClick={() => onCancel(action)}
                        >
                          {a('cancel')}
                        </Button>
                      </Group>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </Panel>
  );
}

function ActionDialog({
  action,
  onClose,
  onSave,
}: {
  action: CompanyNcAction | null;
  onClose: () => void;
  onSave: (input: CompanyNcActionInput) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const a = (key: string) => t(`companyNc.actions.${key}`);
  const [kind, setKind] = useState(action?.kind ?? 'corrective');
  const [description, setDescription] = useState(action?.description ?? '');
  const [responsible, setResponsible] = useState(action?.responsible ?? '');
  const [dueDate, setDueDate] = useState<string | null>(action?.dueDate ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const missing = !description.trim() || !responsible.trim() || !dueDate;
  const submit = async () => {
    if (missing || !dueDate) return;
    setBusy(true);
    setError(null);
    try {
      await onSave({
        kind,
        description: description.trim(),
        responsible: responsible.trim(),
        dueDate,
      });
      onClose();
    } catch (failure) {
      setError(hseErrorText(t, failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      opened
      onClose={onClose}
      title={action ? a('editTitle') : a('addTitle')}
      size="lg"
      closeButtonProps={{ 'aria-label': a('close') }}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <div className={classes.cardBody}>
          <Select
            label={a('kind')}
            required
            data={HSE_ACTION_KINDS.map((value) => ({
              value,
              label: t(`hse.actionKinds.${value}`),
            }))}
            value={kind}
            allowDeselect={false}
            onChange={(next) => setKind(next ?? 'corrective')}
          />
          <Textarea
            label={a('description')}
            required
            autosize
            minRows={2}
            maxLength={2000}
            value={description}
            onChange={(event) => setDescription(event.currentTarget.value)}
          />
          <Group grow align="flex-start">
            <TextInput
              label={a('responsible')}
              required
              maxLength={200}
              value={responsible}
              onChange={(event) => setResponsible(event.currentTarget.value)}
            />
            <DayInput label={a('due')} required value={dueDate} onChange={setDueDate} />
          </Group>
        </div>
        {error ? (
          <p className={classes.banner} data-tone="danger" role="alert">
            {error}
          </p>
        ) : null}
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            {t('hse.dialog.cancel')}
          </Button>
          <Button type="submit" loading={busy} disabled={missing}>
            {a('save')}
          </Button>
        </Group>
      </form>
    </Modal>
  );
}
